// "Is this still {name}?" and "Who is not coming?" (guest profiles P4a,
// 20261002100020): a saved rider is renamed, replaced or removed only by a
// human answer. The client half, shared by every surface that edits riders:
// the widget's checkout (the full page, the drawer, its autosave), the pay
// page and the admin booking editor.
//
// The owner (25 Sept 2026): "We cannot guess which answers belong to which
// guest. It needs to be air tight." Specs: ~/vertu-design/2026-09-25-guest-
// profiles-spec.md §4.1 and §12; ~/vertu-design/2026-09-27-pay-now-amended-
// spec.md §2 C6 and §5.3; ~/vertu-design/2026-09-25-rider-ids-spec.md §5.2 and
// §7.2-7.3.
//
// THE RULES (the server is the judge; this module only asks the question
// before a save the server would refuse, and builds what the answer sends):
//   * THE NAME RULE (a mirror of the server's _participant_identity_keys /
//     _value / _changed): on a list with a private question, a saved rider
//     whose marked name answers change is someone else unless a human says
//     otherwise. With no marker on the list (a hold older than the markers),
//     every question that is not private and has no condition counts.
//   * "IS THIS STILL {name}?":
//       Yes (a spelling fix): staff and above only — the row goes with
//         "_same_person": true, and the rider keeps their id and private
//         answers (owner Q12). The booker never may (owner decision C6):
//         they are told to change it back, or replace the rider.
//       No: Replace — the saved rider is removed with every answer (a
//         tombstone for their id) and a NEW rider is started in their place
//         with only the name just typed; nothing private is ever carried.
//   * "WHO IS NOT COMING?": when more riders are saved than the booking is
//     now for, the guest (or staff) chooses who goes; each is removed by id
//     (a tombstone). A place nobody is in yet that stands in the way goes
//     with them; a saved rider is never dropped without being chosen.
//   * Nothing here is positional: a rider is their id (`_pid`); the number
//     on the card is display only.
//
// Pure: no React, no network, no storage. e2e/rider-rename-logic.spec.ts
// imports it directly.

import {
  formatAnswer,
  isEmptyAnswer,
  isFieldVisible,
  questionsPayload,
  type AnswerRow,
  type GuestNoun,
  type QuestionField,
} from "./booking-questions";
import {
  PARTICIPANT_ID_KEY,
  REMOVE_KEY,
  REPLACES_KEY,
  SAME_NAME_KEY,
  SAME_PERSON_KEY,
  UNDO_ROW_KEY,
  identityValue,
  isPlaceholderRow,
  isRowMarkerKey,
  isTombstone,
  participantIdOf,
  replacedIdOf,
  samePersonHolds,
} from "./participant-ids";

// ─── The name rule (the server's, mirrored) ─────────────────────────────────

/**
 * The questions compared for "is this still the same rider?": the ones
 * marked as the person's name (profile: first_name / last_name / full_name)
 * that are not private for this reader; with no marker on the list, every
 * question that is neither private nor conditional (fails closed). Never a
 * label.
 */
export function identityKeys(fields: readonly QuestionField[]): string[] {
  const marked = fields.filter((f) => f.profile === "first_name" || f.profile === "last_name" || f.profile === "full_name");
  const keys = marked.length
    ? marked.filter((f) => f.sensitive !== true).map((f) => f.key)
    : fields.filter((f) => f.sensitive !== true && !f.show_when).map((f) => f.key);
  return [...keys].sort();
}

// identityValue (an answer as the name rule compares it) lives in
// participant-ids.ts, beside samePersonHolds, which questionsPayload uses.

/** Some name answer differs between `next` and `prev`. */
export function identityChanged(fields: readonly QuestionField[], next: unknown, prev: unknown): boolean {
  const a = (next ?? {}) as Record<string, unknown>;
  const b = (prev ?? {}) as Record<string, unknown>;
  return identityKeys(fields).some((k) => identityValue(a[k]) !== identityValue(b[k]));
}

/** The list asks a private question: the name rule guards its saved riders. */
export function nameRuleGuards(fields: readonly QuestionField[]): boolean {
  return fields.some((f) => f.sensitive === true);
}

/**
 * Does the list mark which questions hold the person's name (profile:
 * first_name / last_name / full_name)? An older list does not: the name rule
 * then compares every plain unconditional answer (a weight too), and the
 * words speak of "details", never "name" (review round).
 */
export function nameMarked(fields: readonly QuestionField[]): boolean {
  return fields.some((f) => f.profile === "first_name" || f.profile === "last_name" || f.profile === "full_name");
}

/**
 * The first question (in the list's own order) that holds the name, for the
 * focus once a name is put back ("Change it back", Undo) or a spelling fix is
 * confirmed. null when the list has none this reader can answer.
 */
export function firstNameKey(fields: readonly QuestionField[]): string | null {
  const keys = new Set(identityKeys(fields));
  return fields.find((f) => keys.has(f.key))?.key ?? null;
}

/** Is this question one the name rule compares (for "asked as the guest leaves the name")? */
export function isNameKey(fields: readonly QuestionField[], key: string): boolean {
  return identityKeys(fields).includes(key);
}

/**
 * Must the question be asked before this row is saved? `fields`: every
 * question the list asks, private ones included (a member below manager: the
 * locked list). `sent`: the row as the save sends it (questionsPayload's).
 * `stored`: the saved rider it names, as this page read it. True when the
 * list is guarded, the row names that very rider (their id), the rider is
 * someone (not a place nobody is in yet), no answer was given yet
 * ("_same_person"), and a name answer changed.
 */
export function renameNeedsAnswer(fields: readonly QuestionField[], sent: unknown, stored: unknown): boolean {
  if (!nameRuleGuards(fields)) return false;
  const id = participantIdOf(sent);
  if (id === null || id !== participantIdOf(stored)) return false;
  if (isPlaceholderRow(stored)) return false;
  // A "same person" counts only for the name it was given for. `sent` is
  // questionsPayload's row: it carries "_same_person" only while
  // samePersonHolds (which fails closed: never without the record of the
  // name it was given for), and never that client-only record. A row that
  // still holds the record (a draft row) is judged by it here.
  const r = sent as Record<string, unknown>;
  if (r[SAME_PERSON_KEY] === true && (!(SAME_NAME_KEY in r) || samePersonHolds(r))) return false;
  return identityChanged(fields, sent, stored);
}

/**
 * The person's name as the booking holds it, for "Is this still {name}?":
 * the marked name questions (first + last, or the full name); with no
 * marker, the usual keys. "" when none is answered (or readable).
 */
export function riderNameOf(fields: readonly QuestionField[], row: unknown): string {
  const r = (row ?? {}) as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");
  const readable = (key: string | undefined) => (key ? fields.some((f) => f.key === key && f.sensitive !== true) : false);
  const bySlot = (slot: string) => fields.find((f) => f.profile === slot && f.sensitive !== true)?.key;
  const pick = (slot: string, keys: readonly string[]) => {
    const marked = bySlot(slot);
    if (marked) return text(r[marked]);
    if (fields.some((f) => f.profile)) return "";
    for (const k of keys) if (readable(k) && text(r[k])) return text(r[k]);
    return "";
  };
  const first = pick("first_name", ["first_name"]);
  const last = pick("last_name", ["surname", "last_name"]);
  return [first, last].filter(Boolean).join(" ") || pick("full_name", ["name", "full_name"]);
}

// ─── Who may do what (the server's rules, mirrored for the buttons) ────────

/**
 * May a private question apply to this rider, judged on the definitions and
 * the plain answers only (the server's _question_row_may_hold_sensitive)? A
 * member below manager may not remove such a rider (a manager can).
 */
export function mayHoldPrivateAnswers(fields: readonly QuestionField[], row: unknown): boolean {
  const privateKeys = new Set(fields.filter((f) => f.sensitive === true).map((f) => f.key));
  const plain: AnswerRow = {};
  for (const [k, v] of Object.entries((row ?? {}) as Record<string, unknown>))
    if (!privateKeys.has(k)) plain[k] = v as AnswerRow[string];
  return fields.some(
    (f) =>
      f.sensitive === true &&
      (!f.show_when || privateKeys.has(f.show_when.field) || isFieldVisible(f, plain)),
  );
}

/**
 * Can a rider hold a private answer and no plain one on this list (a private
 * question, and no required unconditional plain question)? Then a rider this
 * reader sees as empty may still be someone.
 */
export function privateOnlyPossible(fields: readonly QuestionField[]): boolean {
  return nameRuleGuards(fields) && !fields.some((f) => f.sensitive !== true && f.required === true && !f.show_when);
}

// ─── Replace ────────────────────────────────────────────────────────────────

/**
 * "No, someone else": the card becomes a NEW rider in the same place — no
 * id (the server gives a fresh one), only the name answers just typed, and a
 * note of the saved rider it replaces (REPLACES_KEY). Every other answer,
 * private ones included, starts empty. `keepName` false: an empty card
 * (Replace from "Change it back, or replace"). `stored`: the saved rider as
 * this page read it; Undo then gives back the card as it was (UNDO_ROW_KEY:
 * the saved name put back, every other edit kept — review round).
 */
export function replaceRow<R extends Record<string, unknown>>(
  fields: readonly QuestionField[],
  row: R,
  storedId: string,
  keepName = true,
  stored?: unknown,
): Record<string, unknown> {
  const out: Record<string, unknown> = { [REPLACES_KEY]: storedId.toLowerCase() };
  if (keepName) for (const key of identityKeys(fields)) if (row[key] !== undefined && row[key] !== null) out[key] = row[key];
  if (stored !== undefined && stored !== null) {
    const { [UNDO_ROW_KEY]: _undo, [REPLACES_KEY]: _was, ...card } = row as Record<string, unknown>;
    out[UNDO_ROW_KEY] = changeNameBack(fields, card, stored);
  }
  return out;
}

/**
 * "Undo" on a replaced card: the card as it was when Replace was chosen (the
 * saved name back, other edits kept), or else the saved rider as this page
 * read it (with their id).
 */
export function undoReplace<R extends Record<string, unknown>>(row: unknown, stored: R): R {
  const was = row && typeof row === "object" ? (row as Record<string, unknown>)[UNDO_ROW_KEY] : undefined;
  if (was && typeof was === "object" && !Array.isArray(was) && participantIdOf(was) === participantIdOf(stored))
    return { ...(was as R) };
  return { ...stored };
}

/**
 * "Change it back": the saved rider's name answers put back on the card; any
 * other change typed stays. The rider keeps their id.
 */
export function changeNameBack<R extends Record<string, unknown>>(fields: readonly QuestionField[], row: R, stored: unknown): R {
  const was = (stored ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = { ...row };
  for (const key of identityKeys(fields)) {
    if (was[key] === undefined || was[key] === null) delete out[key];
    else out[key] = was[key];
  }
  delete out[SAME_PERSON_KEY];
  delete out[SAME_NAME_KEY];
  return out as R;
}

/**
 * "Yes, fixing a spelling" (staff and above): the row says so on its next
 * save — for the name answers it holds NOW (SAME_NAME_KEY). A name typed
 * after the Yes is not what was confirmed: the Yes no longer counts
 * (samePersonHolds) and the question is asked again (review round).
 */
export function confirmSamePerson<R extends Record<string, unknown>>(fields: readonly QuestionField[], row: R): R {
  const name: Record<string, string> = {};
  for (const key of identityKeys(fields)) name[key] = identityValue(row[key]);
  return { ...row, [SAME_PERSON_KEY]: true, [SAME_NAME_KEY]: name };
}

/** A tombstone: removes the saved rider with this id (a whole-list or a one-rider save). */
export function tombstoneFor(id: string): { _pid: string; _remove: true } {
  return { [PARTICIPANT_ID_KEY]: id.toLowerCase(), [REMOVE_KEY]: true } as { _pid: string; _remove: true };
}

/**
 * What the ONE-RIDER writer is sent for a replaced card (20261002100020,
 * review round): Replace is ONE save, the new rider's answers with the id of
 * the saved rider they replace ({...answers, _replaces: id}): the server
 * deletes the saved rider and stores the new one whole, or stores nothing.
 * A card left empty on a list that asks nothing required is the clear of
 * that place: the tombstone (the server refuses it on a list with a required
 * question, replacement_required). `payload`: the card as questionsPayload
 * sends it.
 */
export function replaceSaveRow(payload: Readonly<AnswerRow>, replacedId: string): AnswerRow {
  const answers = Object.entries(payload).some(([key, value]) => !isRowMarkerKey(key) && value !== null && value !== undefined);
  if (!answers) return tombstoneFor(replacedId) as unknown as AnswerRow;
  const { [PARTICIPANT_ID_KEY]: _id, [REMOVE_KEY]: _remove, [SAME_PERSON_KEY]: _same, ...rest } = payload as Record<string, unknown>;
  return { ...(rest as AnswerRow), [REPLACES_KEY]: replacedId.toLowerCase() };
}

/**
 * A whole-list save's rows: the rows as questionsPayload built them (a
 * replaced card goes without an id: a new rider), then a tombstone for each
 * saved rider a card replaces and for each rider chosen as not coming. The
 * server matches by id, so where a tombstone sits does not matter.
 */
export function withTombstones(
  payload: readonly AnswerRow[],
  draftRows: readonly unknown[],
  removed: readonly string[] = [],
): AnswerRow[] {
  const ids = new Set<string>();
  for (const row of draftRows) {
    const was = replacedIdOf(row);
    if (was) ids.add(was);
  }
  for (const id of removed) ids.add(id.toLowerCase());
  const sentIds = new Set(payload.map((row) => participantIdOf(row)).filter(Boolean));
  const out = payload.filter((row) => !isTombstone(row)).map((row) => ({ ...row }));
  for (const id of ids) if (!sentIds.has(id)) out.push(tombstoneFor(id) as unknown as AnswerRow);
  return out;
}

/** The replaced rider's name, for the card ("replaces Kris"), from the stored rows. */
export function replacedName(fields: readonly QuestionField[], row: unknown, stored: readonly unknown[]): string {
  const was = replacedIdOf(row);
  if (!was) return "";
  return riderNameOf(fields, stored.find((r) => participantIdOf(r) === was));
}

// ─── Who is not coming? ─────────────────────────────────────────────────────

export type NotComingCandidate = {
  id: string;
  /** 1-based place in the stored list (the one-rider writer's number). */
  number: number;
  name: string;
  /**
   * Another candidate has the same name: a plain answer that tells them
   * apart ("Age: 12"), never a private one; "" when none does (the number
   * still does). Review round: two riders both called Ann Rider must never
   * look the same to the person choosing.
   */
  detail?: string;
  /** A member below manager may not remove this rider (a private question may apply). */
  needsManager: boolean;
};

export type NotComing = {
  /** How many of the candidates must be chosen. 0: nobody to choose, only empty places to clear. */
  needed: number;
  /** The riders who are someone, in stored order. */
  candidates: NotComingCandidate[];
  saved: number;
  party: number;
};

/**
 * Does this form hold saved riders past its party (the booking went down
 * under them)? Then no rider of it can be saved until it is settled (the
 * server refuses rows_out_of_line / participant_missing). null when nothing
 * stands in the way (places nobody is in past the party are cleared by the
 * server itself). `withheld`: the reader holds back private answers from
 * this caller, so on a list where a rider may hold only private answers an
 * empty-looking rider may still be someone (counted, never cleared).
 * `strict`: a member below manager (needsManager is worked out).
 */
export function notComingOf(
  fields: readonly QuestionField[],
  stored: readonly unknown[],
  party: number,
  opts: { withheld?: boolean; strict?: boolean } = {},
): NotComing | null {
  if (stored.length <= party) return null;
  const someone = (row: unknown) =>
    !isPlaceholderRow(row) || (!!opts.withheld && participantIdOf(row) !== null && privateOnlyPossible(fields));
  if (!stored.slice(party).some(someone)) return null;
  const candidates: NotComingCandidate[] = [];
  const rows: unknown[] = [];
  stored.forEach((row, i) => {
    const id = participantIdOf(row);
    if (!id || !someone(row)) return;
    rows.push(row);
    candidates.push({
      id,
      number: i + 1,
      name: riderNameOf(fields, row),
      needsManager: !!opts.strict && nameRuleGuards(fields) && mayHoldPrivateAnswers(fields, row),
    });
  });
  // Riders who share a name: a plain answer that tells them apart.
  const byName = new Map<string, number[]>();
  candidates.forEach((c, i) => {
    const key = identityValue(c.name);
    if (key) byName.set(key, [...(byName.get(key) ?? []), i]);
  });
  for (const same of byName.values()) {
    if (same.length < 2) continue;
    const field = distinguishingField(fields, same.map((i) => rows[i]));
    for (const i of same) {
      const row = (rows[i] ?? {}) as Record<string, unknown>;
      candidates[i]!.detail = field && !isEmptyAnswer(field, row[field.key]) ? `${field.label}: ${formatAnswer(field, row[field.key])}` : "";
    }
  }
  return { needed: Math.max(0, candidates.length - party), candidates, saved: candidates.length, party };
}

/**
 * The first question whose answer tells these riders apart: plain (never a
 * private one), not a name, short (a number, a choice, yes/no, a date or a
 * short text), shown and answered for at least one of them.
 */
function distinguishingField(fields: readonly QuestionField[], rows: readonly unknown[]): QuestionField | null {
  const names = new Set(identityKeys(fields));
  const short = new Set(["number", "select", "radio", "yesno", "date", "text"]);
  for (const f of fields) {
    if (f.sensitive === true || names.has(f.key) || !short.has(f.type)) continue;
    const values = rows.map((row) => {
      const r = (row ?? {}) as Record<string, unknown>;
      if (!isFieldVisible(f, r as AnswerRow) || isEmptyAnswer(f, r[f.key])) return "";
      const text = formatAnswer(f, r[f.key]);
      return text.length > 40 ? "" : text;
    });
    if (values.some(Boolean) && new Set(values).size > 1) return f;
  }
  return null;
}

/**
 * The removals that settle the form once `chosen` (ids) are picked, in the
 * order to send them one by one (the one-rider writer): each with the number
 * it has at that moment — from the end, so no removal moves one still to
 * come. The chosen riders, plus any place nobody is in that would still keep
 * a saved rider past the party. Never a saved rider that was not chosen.
 */
export function notComingRemovals(
  fields: readonly QuestionField[],
  stored: readonly unknown[],
  party: number,
  chosen: readonly string[],
  opts: { withheld?: boolean } = {},
): { id: string; number: number }[] {
  const pick = new Set(chosen.map((id) => id.toLowerCase()));
  const someone = (row: unknown) =>
    !isPlaceholderRow(row) || (!!opts.withheld && participantIdOf(row) !== null && privateOnlyPossible(fields));
  const rows = stored.map((row, i) => ({ row, id: participantIdOf(row), number: i + 1 }));
  const out = rows.filter((r) => r.id && pick.has(r.id));
  const left = rows.filter((r) => !(r.id && pick.has(r.id)));
  // Clear places nobody is in (from the front) while a saved rider would
  // still sit past the party.
  const lastSomeone = () => {
    for (let i = left.length - 1; i >= 0; i--) if (someone(left[i]!.row)) return i;
    return -1;
  };
  for (let i = 0; i < left.length && lastSomeone() >= party; ) {
    const r = left[i]!;
    if (r.id && !someone(r.row) && i < lastSomeone()) {
      out.push(r);
      left.splice(i, 1);
    } else i++;
  }
  return out
    .map((r) => ({ id: r.id!, number: r.number }))
    .sort((a, b) => b.number - a.number);
}

// ─── The words ──────────────────────────────────────────────────────────────

function title(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function nounOf(noun: GuestNoun | null | undefined): GuestNoun {
  return noun && noun.one && noun.other ? noun : { one: "guest", other: "guests" };
}

/** "Is this still Kris Rider?" (no name known: "Is this still the same rider?"). */
export function renameQuestion(name: string, noun?: GuestNoun | null): string {
  return name ? `Is this still ${name}?` : `Is this still the same ${nounOf(noun).one}?`;
}

/**
 * Under the question: why it is asked. `who`: "Rider 2". `marked` false (a
 * list that does not mark the name, where a weight counts too): "details".
 */
export function renameWhy(who: string, marked = true): string {
  return marked
    ? `You changed ${who}'s name after their details were saved.`
    : `You changed ${who}'s details after they were saved.`;
}

export const RENAME_YES = "Yes, fixing a spelling";

/** "No, someone else is riding" (for riders), else "No, it's someone else". */
export function renameNo(noun?: GuestNoun | null): string {
  return nounOf(noun).one.trim().toLowerCase() === "rider" ? "No, someone else is riding" : "No, it's someone else";
}

/**
 * The booker answered Yes: they may not rename a saved rider (only the
 * venue can confirm it is the same person). The heading once they have
 * answered (review round: the question they answered does not stay as the
 * heading).
 */
export function bookerRenameTitle(noun?: GuestNoun | null, marked = true): string {
  const g = nounOf(noun);
  return marked ? `A saved ${g.one}'s name can't be changed here` : `A saved ${g.one}'s details can't be changed here`;
}

/**
 * …and the words under it: the two ways on. A spelling mistake is never a
 * dead end (review round: the booker fixing their own name): change it back
 * and book, and the venue corrects it afterwards; someone else riding is a
 * Replace, whose answers go with them.
 */
export function bookerRenameWords(name: string, noun?: GuestNoun | null, marked = true): string {
  const g = nounOf(noun);
  const whom = name || `this ${g.one}`;
  const whose = name ? `${name}'s` : "their";
  return marked
    ? `To keep ${whose} answers with ${whom}, change the name back. If it's only a spelling mistake, book as it is: the venue can correct the name afterwards. If it's someone else, replace this ${g.one} (${whose} answers are removed).`
    : `To keep ${whose} answers with ${whom}, change the details back. If something is wrong, book as it is: the venue can correct it afterwards. If it's someone else, replace this ${g.one} (${whose} answers are removed).`;
}

export const CHANGE_IT_BACK = "Change it back";

/** "Replace this rider". */
export function replaceThis(noun?: GuestNoun | null): string {
  return `Replace this ${nounOf(noun).one}`;
}

/** Staff below manager, on "No": a rider a private question may apply to needs a manager to replace. */
export function replaceNeedsManager(name: string, noun?: GuestNoun | null): string {
  const g = nounOf(noun);
  return `Only a manager can replace ${name || `this ${g.one}`}: their private answers must be removed with them. Change the name back, or ask a manager.`;
}

/** On a replaced card, until it is saved. */
export function replacingNote(name: string, noun?: GuestNoun | null): string {
  const g = nounOf(noun);
  return name
    ? `New ${g.one} in place of ${name}. ${name}'s answers are removed when you save; answer every question for the new ${g.one}.`
    : `New ${g.one}. The saved ${g.one}'s answers are removed when you save; answer every question for the new ${g.one}.`;
}

export const UNDO_REPLACE = "Undo";

/** On a staff-confirmed spelling fix, until it is saved. */
export function samePersonNote(name: string): string {
  return name ? `Same person: ${name}'s name is corrected when you save.` : "Same person: the name is corrected when you save.";
}

/** A save stopped to ask: "Tell us whether Rider 2 is still Kris." */
export function renameAskMessage(who: string, name: string): string {
  return name ? `Please tell us whether ${who} is still ${name}.` : `Please tell us whether ${who} is still the same person.`;
}

export function notComingTitle(): string {
  return "Who is not coming?";
}

/** "This booking is now for 2 riders, but 3 are saved. Choose the rider who is not coming: their answers are removed." */
export function notComingHelp(n: NotComing, noun?: GuestNoun | null): string {
  const g = nounOf(noun);
  const party = `${n.party} ${n.party === 1 ? g.one : g.other}`;
  const which = n.needed === 1 ? `the ${g.one} who is` : `the ${n.needed} ${g.other} who are`;
  return `This booking is now for ${party}, but ${n.saved} are saved. Choose ${which} not coming: their answers are removed.`;
}

/** The button: "Remove 1 rider" / "Remove 2 riders". */
export function notComingButton(n: NotComing, noun?: GuestNoun | null): string {
  const g = nounOf(noun);
  return `Remove ${n.needed} ${n.needed === 1 ? g.one : g.other}`;
}

/** Said while too few or too many are ticked. */
export function notComingChooseCount(n: NotComing, chosen: number, noun?: GuestNoun | null): string | null {
  const g = nounOf(noun);
  if (chosen === n.needed) return null;
  return `Choose ${n.needed} ${n.needed === 1 ? g.one : g.other} (${chosen} chosen).`;
}

/**
 * After the removal: "Removed Ann Rider (was Rider 2) from this booking." —
 * the number they had, so a rider with the same name who stays is never
 * taken for them (review round).
 */
export function notComingDone(removed: readonly Pick<NotComingCandidate, "name" | "number">[], noun?: GuestNoun | null): string {
  const g = nounOf(noun);
  const list = removed.map((r) => (r.name ? `${r.name} (was ${title(g.one)} ${r.number})` : `${title(g.one)} ${r.number}`));
  if (list.length === 0) return `Removed. The booking's ${g.other} are up to date.`;
  const joined = list.length === 1 ? list[0]! : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
  return `Removed ${joined} from this booking.`;
}

/** A save found the form waiting on the question. */
export function notComingFirst(noun?: GuestNoun | null): string {
  return `Please choose who is not coming first: more ${nounOf(noun).other} are saved than this booking is now for.`;
}

/** Staff below manager, on a candidate a private question applies to. */
export function notComingNeedsManager(): string {
  return "a manager must remove them (private answers)";
}

/**
 * Staff below manager where no candidate is theirs to remove: say so, rather
 * than "Choose" with nothing to choose (review round).
 */
export function notComingAskManager(n: NotComing, noun?: GuestNoun | null): string {
  const g = nounOf(noun);
  const party = `${n.party} ${n.party === 1 ? g.one : g.other}`;
  return `This booking is now for ${party}, but ${n.saved} are saved. Their private answers mean a manager must choose who is not coming: please ask a manager.`;
}

/**
 * Staff below manager, and too few of the candidates are theirs to remove
 * (each may hold private answers): nothing to choose, a manager must.
 */
export function notComingBlocked(n: NotComing): boolean {
  return n.needed > 0 && n.candidates.filter((c) => !c.needsManager).length < n.needed;
}

/** The chosen riders as the done words name them (their name and number), in the order chosen. */
export function notComingChosen(n: NotComing, chosen: readonly string[]): Pick<NotComingCandidate, "name" | "number">[] {
  const byId = new Map(n.candidates.map((c) => [c.id, c]));
  return chosen.flatMap((id) => {
    const c = byId.get(id.toLowerCase());
    return c ? [{ name: c.name, number: c.number }] : [];
  });
}

/**
 * The candidates numbered as the page numbers its cards: `first` is the
 * number of this line's first rider on the page (a visit's second line
 * starts after the first line's riders). The id and the stored place are
 * unchanged underneath.
 */
export function notComingNumbered(n: NotComing, first: number): NotComing {
  if (first === 1) return n;
  return { ...n, candidates: n.candidates.map((c) => ({ ...c, number: c.number + first - 1 })) };
}

/**
 * A choice: "Rider 2 · Ann Rider", with a plain answer when another has the
 * same name ("Rider 3 · Ann Rider (Age: 12)"); no name: "Rider 3". The
 * number is the place the page showed them at.
 */
export function candidateLabel(c: NotComingCandidate, noun?: GuestNoun | null): string {
  const n = `${title(nounOf(noun).one)} ${c.number}`;
  const name = c.name ? `${n} · ${c.name}` : n;
  return c.detail ? `${name} (${c.detail})` : name;
}

// ─── Small helpers the surfaces share ───────────────────────────────────────

/** Is this empty for the name rule (the card shows nothing typed for the name)? */
export function nameEmpty(fields: readonly QuestionField[], row: unknown): boolean {
  const r = (row ?? {}) as Record<string, unknown>;
  return identityKeys(fields).every((k) => {
    const f = fields.find((x) => x.key === k);
    return f ? isEmptyAnswer(f, r[k]) : true;
  });
}

/** The row as a save would send it, for the name rule (numbers typed, hidden answers dropped, markers kept). */
export function sentRowOf(fields: QuestionField[], row: AnswerRow): AnswerRow {
  return questionsPayload(fields, [row])[0] ?? {};
}
